<script lang="ts" setup>
import CategoriesService from '../../../pages/store/catalog/categories/_runtime/services/CategoriesService';

defineOptions({
    name: 'WidgetSelectedProductCategoriesSettings'
});

interface FormSettings
{
    settings: {
        categoriesIds: number[];
    };
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    categoriesIds: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };
});

const categoryId = ref<number>(null);

const addCategory = async (): Promise<void> =>
{
    if (categoryId.value == null)
        return;

    if (!form.value.settings.categoriesIds.find(x => x === categoryId.value))
    {
        form.value.settings.categoriesIds.push(categoryId.value);
    }

    categoryId.value = null;
};

const removeCategory = async (id: number): Promise<void> =>
{
    form.value.settings.categoriesIds = form.value.settings.categoriesIds.filter(p => p !== id);
};

const categoriesOptions = computedAsync(async () =>
{
    return await CategoriesService.getCategoriesForTreeSelect();
});

const categories = computedAsync(async () =>
{
    try
    {
        if (form.value.settings.categoriesIds.length === 0)
            return [];

        return await CategoriesService.getCategoriesByIds(form.value.settings.categoriesIds);
    }
    catch (ex)
    {
        return [];
    }
});

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            categoriesIds: schema.array(schema.number().required($t('[[[Wybranie kategorii jest wymagane]]]'))).min(1, $t('[[[Wybranie kategorii jest wymagane]]]')).typeError($t('[[[Wybranie kategorii jest wymagane]]]'))
        })
    }));
}

watch(() => form.value.settings.categoriesIds, () =>
{
    form.value.$errors.clear('settings.categoriesIds');
}, { deep: true });

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup>
        <IdeoFormGroup :label="$t('[[[Wybierz kategorię]]]')" :invalid-feedback="form.$errors.first('categoriesIds')" :state="form.$errors.state('categoriesIds')" required>
            <div class="d-flex flex-column flex-md-row">
                <div class="flex-grow-1">
                    <IdeoFormTreeSelect
                        key="categoryId"
                        v-model="categoryId"
                        name="categories"
                        :options="categoriesOptions"
                        transform
                        placeholder="&nbsp"
                    />
                </div>

                <div class="d-flex align-items-stretch">
                    <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addCategory()">
                        <i class="fas fa-plus" />
                    </button>
                </div>
            </div>
        </IdeoFormGroup>

        <IdeoFormGroup :label="$t('[[[Kategorie]]]')">
            <div v-if="categories" class="list-group">
                <div v-for="item in categories" :key="item.id" class="list-group-item p-2 d-flex gap-1 list-group-item-action'">
                    <div class="d-flex align-items-center me-auto">
                        <span class="me-2">
                            <i class="fad fa-fw fa-browser text-primary" />
                        </span>
                        <span class="me-auto">
                            <span class="d-block" :title="$t('[[[Nazwa kategorii]]]')">
                                <b>{{ item.name }}</b>
                            </span>
                        </span>
                    </div>

                    <span class="d-flex align-items-center">
                        <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-related-${item.id}`" :value="item.id" @confirm="removeCategory(item.id)" />
                        <IdeoButton :id="`btn-delete-related-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń kategorię]]]')">
                            <i class="fas fa-trash-alt" />
                        </IdeoButton>
                    </span>
                </div>
                <div v-if="categories.length == 0" class="list-group-item list-group-item-light text-center">
                    {{ $t('[[[Brak kategorii w grupie]]]') }}
                </div>
            </div>
        </IdeoFormGroup>
    </IdeoFormGroup>
</template>
